import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getMeUid } from '@api/selectors/auth';
import Image from '@base/FutureImage';
import { RING_COLOR } from '@cont/Compete/constants';
import useRatingInfo from '@cont/Compete/useRatingInfo';
import { getUserLevel } from '@cont/Compete/utils';
import styled from '@emotion/styled';
import { getIsDarkModeOn } from '@stores/selectors/darkModeStatusSelectors';
import useDarkModeStatus from '@stores/useDarkModeStatus';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import styleHelper from '@utils/styleHelper';
import Avatar from 'aquilla/core/atoms/Avatar';
import { forwardRef } from 'react';

const Rank = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 70px;
  margin-left: ${Spacings.SPACING_6B};
  margin-right: ${Spacings.SPACING_12B};

  ${mediaQuery.uptoMobile} {
    width: 56px;
    margin-left: ${Spacings.SPACING_2B};
    margin-right: ${Spacings.SPACING_3B};
    text-align: center;
  }
`;

const RankPill = styled.div`
  padding: ${Spacings.SPACING_6} ${Spacings.SPACING_3B};
  background-color: var(--color-divider);
  width: fit-content;
  border-radius: 100px;
  font-weight: ${Fonts.SEMIBOLD};
  font-size: ${Fonts.P1};
  color: var(--color-text-primary);
`;

const Rating = styled.div`
  font-weight: ${Fonts.SEMIBOLD};
  font-size: ${Fonts.H5};
  line-height: 24px;
  color: ${({ $rating, $isMe }) =>
    $rating && !$isMe
      ? 'var(--color-text-secondary)'
      : 'var(--color-text-primary)'};
  min-width: 50px;
  margin-right: ${Spacings.SPACING_6B};
  text-align: right;

  ${mediaQuery.uptoMobile} {
    min-width: 41px;
    margin-right: 0;
    font-weight: ${Fonts.SEMIBOLD};
    font-size: ${Fonts.H6};
    line-height: 20px;
  }
`;

const StyledAvatar = styled(Avatar)`
  border-radius: 50%;
  margin-right: ${Spacings.SPACING_3B};
  width: 40px;
  height: 40px;
`;

const Badge = styled(Image)`
  width: 12px;
  height: 12px;
  margin-right: ${Spacings.SPACING_1B};
`;

const Flex = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: calc(100% - 142px);

  ${mediaQuery.uptoMobile} {
    padding-right: ${Spacings.SPACING_4B};
    width: calc(100% - 76px);
  }
`;

const ListItem = styled.div`
  height: 72px;
  display: flex;
  align-items: center;
  background-color: ${({ $bgColor }) => $bgColor ?? 'var(--color-base-1)'};
`;

const User = styled.div`
  display: flex;
  align-items: center;
  width: 400px;
  ${mediaQuery.uptoMobile} {
    width: auto;
  }
`;

const Details = styled.div`
  display: flex;
  flex-direction: column;
`;

const Name = styled.div`
  color: ${({ $color }) => $color ?? 'var(--color-text-primary)'};
  font-weight: ${Fonts.SEMIBOLD};
  font-size: ${Fonts.P1};
  line-height: 20px;
  display: flex;
  align-items: center;
`;

const StateWrapper = styled.div`
  font-weight: ${Fonts.REGULAR};
  font-size: ${Fonts.P2};
  line-height: 16px;
  color: var(--color-text-primary);
`;

const topRanks = [1, 2, 3];

const LeaderboardItem = forwardRef(({ item = {}, index }, ref) => {
  const { levels } = useRatingInfo();
  const level = getUserLevel(item.rating, levels);
  const isDarkMode = useDarkModeStatus(getIsDarkModeOn);

  const meUid = useMyInfo(getMeUid);
  const isMe = item.userUid === meUid;

  const meIndex = isMe && index + 1;
  const noRatingBadge = isDarkMode
    ? 'compete/no-rating-dark.svg'
    : 'compete/no-rating-light.svg';

  const getBgColor = () => {
    if (isMe) {
      return styleHelper.hexToRgb(RING_COLOR[level?.label], 0.1);
    }

    if (
      (meIndex > 10 && index % 2 !== 0) ||
      (meIndex <= 10 &&
        ((meIndex % 2 === 0 && index % 2 !== 0) ||
          (meIndex % 2 !== 0 && index % 2 === 0)))
    ) {
      return 'var(--color-base-light-0)';
    }
    return 'var(--color-base-1)';
  };

  return (
    <>
      <ListItem
        key={item.userUid}
        $bgColor={getBgColor(meIndex)}
        ref={isMe ? ref : null}
      >
        <Rank>
          {topRanks.includes(item.rank) ? (
            <Image
              src={`/compete/rank/rank-badge-${item.rank}.svg`}
              width={32}
              height={32}
              alt="badge"
            />
          ) : (
            <RankPill>{item.rank ?? '-'}</RankPill>
          )}
        </Rank>
        <Flex>
          <User>
            <StyledAvatar src={item.avatar} alt="user" size="40px" />
            <Details>
              <Name $color={level?.color}>
                <Badge
                  src={level?.badgeImageV1 ?? noRatingBadge}
                  alt="badge"
                  height={12}
                  width={12}
                />
                <div>{isMe ? 'You' : item.firstName}</div>
              </Name>
              <StateWrapper>{item.stateName}</StateWrapper>
            </Details>
          </User>
          <Rating $rating={item.rating} $isMe={isMe}>
            {item.rating ?? 'No Rating'}
          </Rating>
        </Flex>
      </ListItem>
    </>
  );
});

LeaderboardItem.displayName = 'LeaderboardItem';

export default LeaderboardItem;
